import { getScribeHttpClients } from '@heidi/api/client';
import { useQuery } from '@tanstack/react-query';

import { CPD_STATS_KEY } from '../../contracts';

// ============================================================================
// API Response Types
// ============================================================================

/** Response from GET /cpd/stats */
interface CPDStatsResponse {
  total_time_minutes: number;
  total_sources: number;
}

// ============================================================================
// Transport
// ============================================================================

interface ScribeHttp {
  get<T>(url: string): Promise<{ data: T }>;
}

// Resolved PER CALL: the registry is filled at app bootstrap.
const http = (): ScribeHttp => getScribeHttpClients().scribeHttpV2;

// ============================================================================
// Query Hooks
// ============================================================================

interface UseQueryCPDStatsParams {
  enabled?: boolean;
  /** CPD entitlement, resolved by the caller (`useIsCpdQueryEnabled`). */
  isCpdQueryEnabled: boolean;
}

/**
 * Hook to fetch CPD statistics.
 *
 * Uses staleTime: 0 to always refetch on mount since the backend
 * processes pending events synchronously before returning data.
 */
export function useQueryCPDStats({
  enabled = true,
  isCpdQueryEnabled,
}: UseQueryCPDStatsParams) {
  return useQuery<CPDStatsResponse>({
    queryKey: [CPD_STATS_KEY],
    queryFn: async () => {
      const { data } = await http().get<CPDStatsResponse>('/cpd/stats');
      return data;
    },
    enabled: isCpdQueryEnabled && enabled,
    staleTime: 0, // Always refetch - backend processes new events
    refetchOnMount: 'always',
    // SCR-4338: mount refetch already covers freshness — keep this out of the
    // tab-focus / network-reconnect refetch burst that trips the WAF rate limit.
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });
}
